import React, {useCallback, useEffect, useState} from 'react';

import {AddToFolderPopover} from 'app/folders/components/AddToFolderPopover';
import {DeleteDocumentModal} from 'app/general/modals/DeleteDocumentModal';
import {
  ActionButtonBuilderType,
  ActionsButton,
} from 'app/general/widgets/ActionsButton';
import {AddToKnowledgeBasePopover} from 'app/kbs/components/AddToKnowledgeBasePopover';
import {EditTagsDocumentPopover} from 'app/tags/components/EditTagsDocumentPopover';
import {TaggableType} from 'app/tags/types';
import {gt} from 'app/utils/i18n';

import {AddDocumentsToTeamPopover} from './GenericDocumentActionButtons/AddDocumentsToTeamPopover';

import {BaseDocumentContentType, DocumentSerializedType} from 'sweet-types';

interface Props {
  children?: React.ReactElement | ActionButtonBuilderType;
  documents: Array<
    Pick<
      DocumentSerializedType,
      | 'document'
      | 'document_content_type'
      | 'url'
      | 'object_id'
      | 'can_edit'
      | 'can_delete'
    >
  >;
  currentFolder?: void | string;
  refreshData?: () => unknown;
  enableBulkSelect: boolean;
  excludeActions?: Array<
    'edit-tags' | 'folders' | 'teams' | 'knowledge-base' | 'recycle'
  >;
}

/* a button which opens a dropdown menu with avilable actions for the current document */
export function DocumentBulkActionsButton({
  children,
  documents,
  currentFolder,
  refreshData,
  enableBulkSelect,
  excludeActions = [],
}: Props): JSX.Element | null {
  if (!enableBulkSelect) {
    return null;
  }
  const [isShowing, setShow] = useState<
    null | 'knowledge-base' | 'delete' | 'folders' | 'edit-tags' | 'teams'
  >(null);

  const close = () => setShow(null);

  const [currButtonRef, setCurrButtonRef] = useState<null | HTMLElement>(null);

  const setButtonRef = useCallback((newButtonRef) => {
    if (newButtonRef != currButtonRef) {
      setCurrButtonRef(newButtonRef);
    }
  }, []);

  const availableActions = React.useMemo(() => {
    return [
      {
        icon: 'fa fa-tag',
        text: gt('Edit tags (%s selected)', documents.length),
        key: 'edit-tags',
        disabled:
          !documents.every(
            (doc) =>
              ['procedure', 'policy', 'proceduregroup'].includes(
                doc.document_content_type
              ) && doc.can_edit
          ) || documents.length === 0,
        callback: () => setShow('edit-tags'),
        condition: () => !excludeActions.includes('edit-tags'),
      },
      {
        icon: 'far fa-folder',
        text: gt('Folders (%s selected)', documents.length),
        key: 'folders',
        disabled:
          !documents.every(
            (doc) =>
              ['procedure', 'policy', 'proceduregroup'].includes(
                doc.document_content_type
              ) && doc.can_edit
          ) || documents.length === 0,
        callback: () => setShow('folders'),
        condition: () => !excludeActions.includes('folders'),
      },
      {
        icon: 'fa fa-users',
        text: gt('Teams (%s selected)', documents.length),
        key: 'teams',
        disabled:
          !documents.every(
            (doc) =>
              ['procedure', 'policy', 'proceduregroup'].includes(
                doc.document_content_type
              ) && doc.can_edit
          ) || documents.length === 0,
        callback: () => setShow('teams'),
        condition: () => !excludeActions.includes('teams'),
      },
      {
        icon: 'far fa-book-open',
        text: gt('Knowledge Base (%s selected)', documents.length),
        key: 'knowledge-base',
        disabled:
          !documents.every(
            (doc) =>
              ['procedure', 'policy'].includes(doc.document_content_type) &&
              doc.can_edit
          ) || documents.length === 0,
        callback: () => setShow('knowledge-base'),
        condition: () => !excludeActions.includes('knowledge-base'),
      },
      {
        icon: 'far fa-trash',
        text: gt('Move to Recycle Bin (%s selected)', documents.length),
        key: 'recycle',
        disabled:
          !documents.every((doc) => doc.can_delete) || documents.length === 0,
        callback: () => setShow('delete'),
        condition: () => !excludeActions.includes('recycle'),
      },
    ];
  }, [documents]);

  useEffect(() => {
    if (documents.length === 0) {
      close();
    }
  }, [documents]);

  // these props are used on all popovers
  const popoversOverlayProps = {
    target: currButtonRef as HTMLElement,
    trigger: 'legacy',
    placement: 'bottom' as const,
    isOpen: true,
    toggle: close,
  };

  const promisedCallback = () =>
    Promise.resolve(refreshData != null ? refreshData() : null);

  return (
    <>
      <ActionsButton ref={setButtonRef} actions={availableActions}>
        {children}
      </ActionsButton>
      {isShowing == null || currButtonRef == null ? null : isShowing ===
        'delete' ? (
        <DeleteDocumentModal
          show={true}
          documents={documents}
          redirectContentType={'document'}
          onClose={close}
          onDelete={promisedCallback}
        />
      ) : isShowing === 'knowledge-base' ? (
        <AddToKnowledgeBasePopover
          overlayProps={{...popoversOverlayProps, trigger: 'click'}}
          documents={documents.map((doc) => ({
            object_id: doc.object_id,
            document_content_type:
              doc.document_content_type as BaseDocumentContentType,
            url: doc.document.url,
          }))}
          onSuccess={promisedCallback}
        />
      ) : isShowing === 'folders' ? (
        <AddToFolderPopover
          overlayProps={{...popoversOverlayProps, trigger: 'click'}}
          onSuccess={promisedCallback}
          documents={documents.map((doc) => ({
            object_id: doc.object_id,
            document_content_type:
              doc.document_content_type as BaseDocumentContentType,
            url: doc.document.url,
          }))}
          currentFolder={currentFolder}
        />
      ) : isShowing === 'edit-tags' ? (
        <EditTagsDocumentPopover
          overlayProps={popoversOverlayProps}
          documents={documents.map((doc) => ({
            object_id: doc.object_id,
            document_content_type: doc.document_content_type as TaggableType,
            tags: doc.document.tags,
          }))}
          onSuccess={promisedCallback}
        />
      ) : isShowing == 'teams' ? (
        <AddDocumentsToTeamPopover
          documentURLs={documents.map((document) => document.url)}
          documentObjectURLs={documents.map(
            (document) => document.document.url
          )}
          teamMemberships={documents.flatMap(
            (document) => document.document.team_memberships
          )}
          onSuccess={promisedCallback}
          overlayProps={popoversOverlayProps}
        />
      ) : null}
    </>
  );
}
